import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))

            // check for previous reload time not to reload infinitely
            if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
              window.sessionStorage.setItem('nuxt-reload', timeNow)
              window.location.reload(true /* skip cache */)
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"HOST_NODE_IP":"10.71.77.148","INTEGRATION_TEST":"","SITE_ID":"56cf017d-5b49-4baa-93a5-9ace39856221","LANGUAGE":"en_US:en","npm_config_user_agent":"npm/8.19.3 node/v16.19.0 linux x64 workspaces/false ci/netlify","RVM_DIR":"/opt/buildhome/.rvm","CI":"true","NODE_VERSION":"v16.19.0","YARN_VERSION":"1.22.19","URL":"https://oregacha.com","NETLIFY_IMAGES_CDN_DOMAIN":"d33wubrfki0l68.cloudfront.net","BRANCH":"old_production","npm_node_execpath":"/opt/buildhome/.nvm/versions/node/v16.19.0/bin/node","GIMME_NO_ENV_ALIAS":"true","MAINTENANCE_MODE":"false","SHLVL":"1","NETLIFY_SUBSTAGE_TIMES":"/tmp/substage_times.txt","npm_config_noproxy":"","HOME":"/opt/buildhome","OLDPWD":"/opt/build/repo/frontend2","DOMAIN":"oregacha.com","NVM_BIN":"/opt/buildhome/.nvm/versions/node/v18.17.1/bin","npm_package_json":"/opt/build/repo/frontend2/package.json","NVM_INC":"/opt/buildhome/.nvm/versions/node/v18.17.1/include/node","rvm_stored_umask":"0022","CACHED_COMMIT_REF":"70a66452f5dcec26b80339edfba9f8f32e50ae09","SQUARE_APP_ID":"sq0idp-XZo1LUsfr6Kde2TBR0AWJQ","GIMME_TYPE":"binary","GIMME_ENV_PREFIX":"/opt/buildhome/.gimme/env","rvm_user_install_flag":"1","PIPENV_RUNTIME":"3.8","npm_config_userconfig":"/opt/buildhome/.npmrc","npm_config_local_prefix":"/opt/build/repo/frontend2","GOROOT":"/opt/buildhome/.gimme/versions/go1.19.12.linux.amd64","COMMIT_REF":"4ba67bd25d26d5a4737215036cd23492253ac2ef","COLOR":"0","NVM_DIR":"/opt/buildhome/.nvm","npm_config_metrics_registry":"https://registry.npmjs.org/","GA4_KEY":"G-E798K97LKD","GIMME_GO_VERSION":"1.19.x","CUSTOM_RUBY":"0","NETLIFY_BUILD_BASE":"/opt/build","DEPLOY_URL":"https://64f49e1dd2209b2172574aa4--gacha4u-production.netlify.app","FORCE_COLOR":"1","_":"/opt/buildhome/.nvm/versions/node/v16.19.0/bin/npm","rvm_bin_path":"/opt/buildhome/.rvm/bin","npm_config_prefix":"/opt/buildhome/.nvm/versions/node/v16.19.0","RUBY_VERSION":"2.7.2","STRIPE_PUBLIC_KEY":"pk_test_51Mq9v5ItMsv4TfWkdmTGnK3xJxQfmWboQMXK6uzeucwk0el8c2M52UVwC9crUabtKA9YSZ6g626sgtYcjxq4jmHo00kqD0bl3X","IRBRC":"/opt/buildhome/.rvm/rubies/ruby-2.7.2/.irbrc","npm_config_cache":"/opt/buildhome/.npm","GO_VERSION":"1.19.x","npm_config_node_gyp":"/opt/buildhome/.nvm/versions/node/v16.19.0/lib/node_modules/npm/node_modules/node-gyp/bin/node-gyp.js","PATH":"/opt/build/repo/frontend2/node_modules/.bin:/opt/build/repo/node_modules/.bin:/opt/build/node_modules/.bin:/opt/node_modules/.bin:/node_modules/.bin:/opt/buildhome/.nvm/versions/node/v16.19.0/lib/node_modules/npm/node_modules/@npmcli/run-script/lib/node-gyp-bin:/opt/build/repo/frontend2/node_modules/.bin:/opt/build/repo/node_modules/.bin:/opt/build/node_modules/.bin:/opt/node_modules/.bin:/node_modules/.bin:/opt/buildhome/.nvm/versions/node/v16.19.0/bin:/opt/build/repo/frontend2/node_modules/.bin:/opt/buildhome/.nvm/versions/node/v16.19.0/bin:/opt/buildhome/.gimme/versions/go1.19.12.linux.amd64/bin:/opt/buildhome/.rvm/gems/ruby-2.7.2/bin:/opt/buildhome/.rvm/gems/ruby-2.7.2@global/bin:/opt/buildhome/.rvm/rubies/ruby-2.7.2/bin:/opt/buildhome/python3.8/bin:/home/linuxbrew/.linuxbrew/bin:/opt/buildhome/.swiftenv/bin:/opt/buildhome/.swiftenv/shims:/opt/buildhome/.php:/opt/buildhome/.binrc/bin:/opt/buildhome/.deno/bin:/usr/local/rvm/bin:/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin:/opt/buildhome/.cask/bin:/opt/buildhome/.gimme/bin:/opt/buildhome/.dotnet/tools:/opt/buildhome/.dotnet:/opt/buildhome/.cargo/bin:/opt/buildhome/.rvm/bin","NEXT_TELEMETRY_DISABLED":"1","NODE":"/opt/buildhome/.nvm/versions/node/v16.19.0/bin/node","npm_package_name":"oregacha","CONTEXT":"production","GIMME_CGO_ENABLED":"true","HEAD":"old_production","PULL_REQUEST":"false","BUILD_ID":"64f49e1dd2209b2172574aa2","MY_RUBY_HOME":"/opt/buildhome/.rvm/rubies/ruby-2.7.2","DEPLOY_PRIME_URL":"https://old-production--gacha4u-production.netlify.app","LANG":"en_US.UTF-8","CYPRESS_CACHE_FOLDER":"./node_modules/.cache/CypressBinary","npm_lifecycle_script":"nuxt generate && cp -r ./static/.well-known ./dist/ && sh sourcemap.sh","HOST":"https://oldapi.oregacha.com","GOCACHE":"/opt/buildhome/.gimme_cache/gocache","GOPATH":"/opt/buildhome/.gimme_cache/gopath","npm_package_version":"1.0.0","npm_lifecycle_event":"generate","rvm_prefix":"/opt/buildhome","rvm_loaded_flag":"1","GEM_HOME":"/opt/buildhome/.rvm/gems/ruby-2.7.2","DEPLOY_ID":"64f49e1dd2209b2172574aa4","rvm_version":"1.29.12 (latest)","BUGSNAG_KEY_BUILD_INFO":"8b2ab336661bf29d58011117d9c997d3","PIPENV_DEFAULT_PYTHON_VERSION":"3.8","BUGSNAG_API_KEY":"c137351878da32ce105531fa250a3d57","VIRTUAL_ENV":"/opt/buildhome/python3.8","NETLIFY":"true","REPOSITORY_URL":"https://github.com/Hijiri-K/gacha_docker","SITE_NAME":"gacha4u-production","npm_config_globalconfig":"/opt/buildhome/.nvm/versions/node/v16.19.0/etc/npmrc","npm_config_init_module":"/opt/buildhome/.npm-init.js","PIPENV_VENV_IN_PROJECT":"1","PWD":"/opt/build/repo/frontend2","GEM_PATH":"/opt/buildhome/.rvm/gems/ruby-2.7.2:/opt/buildhome/.rvm/gems/ruby-2.7.2@global","LC_ALL":"en_US.UTF-8","npm_execpath":"/opt/buildhome/.nvm/versions/node/v16.19.0/lib/node_modules/npm/bin/npm-cli.js","SQUARE_LOCATION_ID":"LECTF1YC4FV7J","NVM_CD_FLAGS":"","npm_config_global_prefix":"/opt/buildhome/.nvm/versions/node/v16.19.0","npm_command":"run-script","FEATURE_FLAGS":"bitballoon_manifest_backfill,edge_functions_system_logger,bitballoon_allow_adding_dns_zone_with_shared_ownership,edge_functions_correct_order,bitballoon_edge_functions_usage,project_rhyhorn_ui,project_skarmory_ui,project_jigglypuff_ui,reviewer_as_a_member,bitballoon_show_deploy_expiry_enabled,project_jiktomite_ui,ops_advanced_deploy_filtering_ui,bitballoon_disable_purge_related_certs,bitballoon_websocket_for_first_deploy,project_archen_ui,bitballoon_traffic_exceptions_enabled,bitballoon_build_settings_multiple_events,project_wobbuffet_ui,buildbot_tracing_in_ntl_build,edge_functions_cache_cli,bitballoon_resolve_custom_domain_suffixes,bitballoon_purge_via_netlify_server,project_jirachi_ui,edge_functions_bootstrap_log_passthrough_errors,project_moltres_ui,project_dewgong_ui,bitballoon_increase_deploy_files_slice,project_lucario_bitballoon,project_snubbull_ui,bitballoon_get_new_zone_info_on_create,bitballoon_mime_type_use_marcel,buildbot_stage_install_dependencies,project_fomantis_ui,buildbot_firebase_bulk_flush,zisi_disallow_new_entry_name,bitballoon_silent_deploy_cancellation,project_drakloak_ui,project_sunflora_ui,edge_functions_path_urlpattern,bitballoon_custom_function_routes,bitballoon_use_analytics_for_functions_billing,bitballoon_apply_account_password_fix,project_cleffa_ui,project_toxapex_ui,buildbot_js_workspace_cache,project_stoutland_ui,bitballon_disable_asset_optimization_for_new_sites,bitballoon_edge_functions_billing,bitballoon_asset_optimization_deprecation,project_deploy_configuration_api_use_per_function_configuration_files,edge_functions_bootstrap_strip_content_length,project_sunkern_ui,project_vulpix_ui,bitballoon_use_analytics_for_build_minutes_usage,proxy-remove-limit-on-streaming-response-size-for-functions,project_omastar_ui,project_clodhopper_ui,edge_functions_config_export,bitballoon_exclude_deploy_previews_from_deploy_badge,project_roots_ui,gatsby_plugin_prerelease,bitballoon-enterprise-trials,bitballoon_single_player_starter,project_treecko_ui,buildbot_zisi_system_log,project_cursola_ui,project_kakuna_ui,project_thwackey_ui,bitballoon-feature-flag-test,verify_domain_using_edge_challenge,edge_functions_cache_deno_dir,bitballoon_new_accounts_use_envelope,project_alomomola_ui,project_yamper_ui,project_nidorina_ui,bitballoon_save_all_members,bitballoon_auto_accept_git_committers,bitballoon_send_pro_trial_ended_email,project_golduck_ui,project_snorlax_ui,bitballoon_deploy_delete_notifications,bitballoon_prevent_sites_using_envelope_from_calling_old_env,bitballoon_account_level_custom_domain_suffix,bitballoon_branch_as_subdomain_for_buildbot_deploys,buildbot_gatsby_telemetry_flag,bitballoon_zuora_contact_geolocation_sandbox,bitballoon_deploy_retention_soft_delete,bitballoon_periodic_soft_delete_deploys,project_darkrai_ui,project_razz_ui,cli_fetch_integrations,project_flamigo_ui,edge_functions_bootstrap_bypass_response_headers,bitballoon_use_analytics_for_build_minutes_top_queries,bitballoon_lambda_states_enabled,bitballoon_delegate_subdomain_to_netlify,bitballoon_do_not_error_ready_deploy,no-prefix-cli-test-ff,bitballoon_filter_deleted_builds,bitballoon_soft_delete_errored_deploys,build_fetch_integrations,bitballoon_mining_detection_enabled,bitballoon_background_envelope_request,bitballoon_deploy_transition_notifications,bitballoon_extended_domain_validations,bitballoon-disable-cache-network,build_plugins_use_prerelease,bitballoon_zuora_contact_geolocation,bitballoon_delete_firebase_deploy_logs,buildbot_fetch_integrations,edge_functions_invalid_config_throw,zisi_use_latest_babel_version,zisi_log_dynamic_imports,zisi_functions_api_v2,bitballoon_custom_deploy_retention_enabled,functions_inherit_build_nodejs_version,project_bonsly_ui,project_lucario_ui,project_exeggcute_ui,project_tentacruel_ui","SWIFTENV_ROOT":"/opt/buildhome/.swiftenv","rvm_path":"/opt/buildhome/.rvm","INIT_CWD":"/opt/build/repo/frontend2","EDITOR":"vi","NODE_ENV":"production","_applied":"true","VUE_ENV":"server","_AXIOS_BASE_URL_":"/"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/assign
          window.location.assign(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
      app.context.beforeSerialize = fn => context.beforeSerializeFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext) {
  if (!promises.length || appContext._redirected || appContext._errored) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
